import { TimeSlotName } from '../services/treatmentAdministration';

export interface FrequencySlotConfig {
  names: TimeSlotName[];
  labels: string[]; // translation keys for abstract slots, literal strings for hour-based
  isHourBased: boolean;
}

/**
 * Maps frequency values to their time slot configurations.
 * Only used for abstract (non-hour-based) frequencies.
 * Hour-based frequencies use generateHourSlots() for dynamic generation.
 */
const ABSTRACT_FREQUENCY_MAP: Record<string, { names: TimeSlotName[]; labelKeys: string[]; isHourBased: boolean }> = {
  '24h': {
    names: ['morning'],
    labelKeys: ['hospital.treatment-inpatient-time-slot-morning'],
    isHourBased: false,
  },
  '12h': {
    names: ['morning', 'evening'],
    labelKeys: [
      'hospital.treatment-inpatient-time-slot-morning',
      'hospital.treatment-inpatient-time-slot-evening',
    ],
    isHourBased: false,
  },
  '8h': {
    names: ['morning', 'noon', 'evening'],
    labelKeys: [
      'hospital.treatment-inpatient-time-slot-morning',
      'hospital.treatment-inpatient-time-slot-noon',
      'hospital.treatment-inpatient-time-slot-evening',
    ],
    isHourBased: false,
  },
  '6h': {
    names: ['morning', 'noon', 'evening', 'bedtime'],
    labelKeys: [
      'hospital.treatment-inpatient-time-slot-morning',
      'hospital.treatment-inpatient-time-slot-noon',
      'hospital.treatment-inpatient-time-slot-evening',
      'hospital.treatment-inpatient-time-slot-bedtime',
    ],
    isHourBased: false,
  },
  'nocte': {
    names: ['bedtime'],
    labelKeys: ['hospital.treatment-inpatient-time-slot-bedtime'],
    isHourBased: false,
  },
  'stat': {
    names: ['morning', 'noon', 'evening', 'bedtime'],
    labelKeys: [
      'hospital.treatment-inpatient-time-slot-morning',
      'hospital.treatment-inpatient-time-slot-noon',
      'hospital.treatment-inpatient-time-slot-evening',
      'hospital.treatment-inpatient-time-slot-bedtime',
    ],
    isHourBased: false,
  },
  'prn': {
    names: ['morning', 'noon', 'evening', 'bedtime'],
    labelKeys: [
      'hospital.treatment-inpatient-time-slot-morning',
      'hospital.treatment-inpatient-time-slot-noon',
      'hospital.treatment-inpatient-time-slot-evening',
      'hospital.treatment-inpatient-time-slot-bedtime',
    ],
    isHourBased: false,
  },
};

// Default config for unknown frequencies
const DEFAULT_CONFIG = ABSTRACT_FREQUENCY_MAP['6h'];

const HOUR_BASED_FREQUENCIES: Record<string, number> = {
  '4h': 4,
  '2h': 2,
  '48h': 48,
  '72h': 72,
  'q6h': 6,
  'q8h': 8,
  'q12h': 12,
  'q24h': 24,
};

/**
 * Generates hour-based time slot names and labels from a start hour and interval.
 * For intervals >= 24h (Q48H, Q72H), returns a single slot.
 */
export function generateHourSlots(startHour: number, intervalHours: number): { names: TimeSlotName[]; labels: string[] } {
  if (intervalHours >= 24) {
    const name = `h${String(startHour).padStart(2, '0')}` as TimeSlotName;
    const label = `${String(startHour).padStart(2, '0')}:00`;
    return { names: [name], labels: [label] };
  }
  const count = 24 / intervalHours;
  const names: TimeSlotName[] = [];
  const labels: string[] = [];
  for (let i = 0; i < count; i++) {
    const hour = (startHour + i * intervalHours) % 24;
    names.push(`h${String(hour).padStart(2, '0')}` as TimeSlotName);
    labels.push(`${String(hour).padStart(2, '0')}:00`);
  }
  return { names, labels };
}

/**
 * Returns time slot names and display labels for a given frequency.
 * @param frequency - The frequency value (e.g., '4h', '12h', '48h')
 * @param translate - Translation function for abstract slot labels
 * @param startTime - Optional start time hour string (e.g., "06", "08") for hour-based frequencies
 */
export function getTimeSlotsForFrequency(
  frequency: string,
  translate: (key: string) => string,
  startTime?: string | null
): FrequencySlotConfig {
  // Check if this is an hour-based frequency
  const interval = HOUR_BASED_FREQUENCIES[frequency];
  if (interval !== undefined) {
    const startHour = parseStartTime(startTime, frequency);
    const { names, labels } = generateHourSlots(startHour, interval);
    return { names, labels, isHourBased: true };
  }

  // Abstract frequency — use static map
  const config = ABSTRACT_FREQUENCY_MAP[frequency] || DEFAULT_CONFIG;
  const labels = config.labelKeys.map((key) => translate(key));
  return {
    names: config.names,
    labels,
    isHourBased: config.isHourBased,
  };
}

/**
 * Checks if a given day is an active administration day for multi-day frequencies.
 * Q48H: active on even-numbered days from treatment start.
 * Q72H: active every 3rd day from treatment start.
 * All other frequencies: always active.
 *
 * @param frequency - The frequency value
 * @param day - The calendar day to check
 * @param treatmentStartDate - The treatment's start date (reference point)
 */
export function isDayActiveForFrequency(
  frequency: string,
  day: Date,
  treatmentStartDate?: Date | string | null
): boolean {
  if (frequency !== '48h' && frequency !== '72h') {
    return true;
  }

  if (!treatmentStartDate) {
    return true; // Graceful fallback: all days active if no start date
  }

  const start = typeof treatmentStartDate === 'string'
    ? new Date(treatmentStartDate + 'T00:00:00')
    : treatmentStartDate;

  // Normalize both dates to midnight UTC to avoid timezone issues
  const startDay = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
  const checkDay = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()));

  const diffMs = checkDay.getTime() - startDay.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  // Before treatment start date: never active
  if (diffDays < 0) {
    return false;
  }

  if (frequency === '48h') {
    return diffDays % 2 === 0;
  }

  if (frequency === '72h') {
    return diffDays % 3 === 0;
  }

  return true;
}

/**
 * Returns true if this frequency uses hour-based time slot identifiers.
 */
export function isHourBasedFrequency(frequency: string): boolean {
  return frequency in HOUR_BASED_FREQUENCIES;
}

/**
 * Returns the valid time slot names for a given frequency.
 * @param startTime - Optional start time hour string for hour-based frequencies
 */
export function getValidTimeSlotsForFrequency(frequency: string, startTime?: string | null): TimeSlotName[] {
  const interval = HOUR_BASED_FREQUENCIES[frequency];
  if (interval !== undefined) {
    const startHour = parseStartTime(startTime, frequency);
    return generateHourSlots(startHour, interval).names;
  }
  const config = ABSTRACT_FREQUENCY_MAP[frequency] || DEFAULT_CONFIG;
  return config.names;
}

/**
 * Parses a start time string into a valid hour number (0-23).
 * Defaults to 6 for most frequencies, 8 for Q48H/Q72H (backward compat with pre-startTime behavior).
 */
function parseStartTime(startTime?: string | null, frequency?: string): number {
  if (!startTime) {
    // Q48H/Q72H previously defaulted to h08
    if (frequency === '48h' || frequency === '72h') return 8;
    return 6;
  }
  const parsed = parseInt(startTime, 10);
  if (isNaN(parsed) || parsed < 0 || parsed > 23) {
    if (frequency === '48h' || frequency === '72h') return 8;
    return 6;
  }
  return parsed;
}
